import React, { useContext, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikContextType } from 'formik';
import FormikTextField from '../../components/formikComponents/FormikTextField';
import FormikSelect from '../../components/formikComponents/FormikSelect';
import { UIContext } from '../../context/UIContext';
import FormikDatePicker from '../../components/formikComponents/FormikDatePicker';
import FormikPhoneNumber from '../../components/formikComponents/FormikPhoneNumber';
import AddressFormWithGoogleAutocomplete from '../../components/AddressFormWithGoogleAutocomplete';

const PersonalInformationForm = ({
  formik,
  includeEmailField,
}: {
  formik: FormikContextType<any>;
  includeEmailField: boolean;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useContext(UIContext);
  const [validMobileNumber, setValidMobileNumber] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string>('');

  const titleOptions = [
    { value: 'mr', label: t('screens.personalInformation.titles.mr') },
    { value: 'mrs', label: t('screens.personalInformation.titles.mrs') },
    { value: 'ms', label: t('screens.personalInformation.titles.ms') },
    { value: 'miss', label: t('screens.personalInformation.titles.miss') },
    { value: 'dr', label: t('screens.personalInformation.titles.dr') },
    { value: 'sir', label: t('screens.personalInformation.titles.sir') },
    { value: 'master', label: t('screens.personalInformation.titles.master') },
    { value: 'prof', label: t('screens.personalInformation.titles.prof') },
    { value: 'rev', label: t('screens.personalInformation.titles.rev') },
    { value: 'mx', label: t('screens.personalInformation.titles.mx') },
  ];

  const roleOptions = [
    { value: 'Business Owner', label: t('screens.personalInformation.roles.owner') },
    { value: 'Accountant', label: t('screens.personalInformation.roles.accountant') },
    { value: 'Manager', label: t('screens.personalInformation.roles.manager') },
    { value: 'CEO', label: t('screens.personalInformation.roles.ceo') },
    { value: 'COO', label: t('screens.personalInformation.roles.coo') },
    { value: 'CFO', label: t('screens.personalInformation.roles.cfo') },
    { value: 'Other', label: t('screens.personalInformation.roles.other') },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: isMobile ? 0 : '1rem',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <FormikSelect
          label={t('screens.personalInformation.title')}
          id="title"
          name="title"
          formName="wizardPersonalInformation"
          required
          options={titleOptions}
        />
        <FormikTextField
          label={t('screens.personalInformation.firstName')}
          id="firstName"
          name="firstName"
          formName="wizardPersonalInformation"
          required
        />
      </Box>
      <FormikTextField
        label={t('screens.personalInformation.lastName')}
        id="lastName"
        name="lastName"
        formName="wizardPersonalInformation"
        required
      />
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: isMobile ? 0 : '1rem',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <FormikSelect
          label={t('screens.personalInformation.role')}
          id="role"
          name="role"
          formName="wizardPersonalInformation"
          required
          options={roleOptions}
        />
        {includeEmailField && (
          <FormikTextField
            label={t('screens.personalInformation.emailAddress')}
            id="emailAddress"
            name="emailAddress"
            formName="wizardPersonalInformation"
            required
          />
        )}
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: isMobile ? 0 : '1rem',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <FormikDatePicker
          label={t('screens.personalInformation.dateOfBirth')}
          id="dateOfBirth"
          name="dateOfBirth"
          formName="wizardPersonalInformation"
          required
          type="text"
          disableFuture
          fieldSx={{ width: '100%' }}
        />
        <FormikPhoneNumber
          label={t('screens.personalInformation.phoneNumber')}
          id="phoneNumber"
          type="tel"
          formName="wizardPersonalInformation"
          autoComplete="tel"
          required
          name="phoneNumber"
          error={phoneNumberError}
          clearError={() => {
            setPhoneNumberError('');
          }}
          setError={() => setPhoneNumberError(t('validation.invalidPhoneNumber'))}
          valid={validMobileNumber}
          setValid={setValidMobileNumber}
          defaultCountry={
            formik?.values?.country && formik?.values?.country?.length === 2
              ? formik?.values?.country?.toLowerCase()
              : 'gb'
          }
        />
      </Box>
      <AddressFormWithGoogleAutocomplete
        formik={formik}
        formName="wizardPersonalInformation"
        translationKey="screens.personalInformation.address"
      />
    </Box>
  );
};

export default PersonalInformationForm;
